import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import api, { getAssetUrl } from "../../services/api";

const MAX_FOTO_SIZE_BYTES = 100 * 1024;
const MAX_FOTO_WIDTH = 600;
const MAX_FOTO_HEIGHT = 600;

async function gerarBlobJpeg(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (result) => {
        if (result) {
          resolve(result);
          return;
        }
        reject(new Error("Não foi possível gerar a imagem otimizada."));
      },
      "image/jpeg",
      quality
    );
  });
}

async function redimensionarFoto(file) {
  const imageUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Não foi possível carregar a imagem."));
      img.src = imageUrl;
    });

    const scale = Math.min(MAX_FOTO_WIDTH / image.width, MAX_FOTO_HEIGHT / image.height, 1);
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Canvas não disponível para tratar a imagem.");
    }

    let currentScale = scale;
    let blob = null;

    while (currentScale > 0.1) {
      const width = Math.max(1, Math.round(image.width * currentScale));
      const height = Math.max(1, Math.round(image.height * currentScale));

      canvas.width = width;
      canvas.height = height;
      context.clearRect(0, 0, width, height);
      context.drawImage(image, 0, 0, width, height);

      for (const quality of [0.82, 0.72, 0.62, 0.52, 0.42, 0.32]) {
        blob = await gerarBlobJpeg(canvas, quality);
        if (blob.size <= MAX_FOTO_SIZE_BYTES) {
          break;
        }
      }

      if (blob && blob.size <= MAX_FOTO_SIZE_BYTES) {
        break;
      }

      currentScale *= 0.85;
    }

    if (!blob || blob.size > MAX_FOTO_SIZE_BYTES) {
      throw new Error("Não foi possível reduzir a foto para menos de 100 KB.");
    }

    const originalName = (file.name || "foto").replace(/\.[^.]+$/, "");
    return new File([blob], `${originalName}.jpg`, { type: "image/jpeg" });
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
}

async function criarArquivoDaFotoDoUsuario(usuario) {
  const fotoUrl = getAssetUrl(usuario?.foto_url);
  if (!fotoUrl) {
    return null;
  }

  const response = await fetch(fotoUrl);
  if (!response.ok) {
    throw new Error("Não foi possível carregar a foto do usuário.");
  }

  const blob = await response.blob();
  const nomeBase = (usuario?.nome || "usuario").trim().replace(/\s+/g, "_").toLowerCase();
  return new File([blob], `${nomeBase || "usuario"}.jpg`, {
    type: blob.type || "image/jpeg",
  });
}

export default function CadastroFacialModal({
  isOpen,
  onClose,
  dispositivos,
  grupo,
  moradores,
  visitantes,
  contratados,
  usuarioPreSelecionado,
}) {
  const [usuarioKey, setUsuarioKey] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [foto, setFoto] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const listaDispositivos = dispositivos || [];
  const isGrupo = Boolean(grupo);
  const dispositivo = listaDispositivos[0] || null;
  const usuariosBase = [
    ...(moradores || []).map((morador) => ({
      key: `morador-${morador.id}`,
      tipo: "morador",
      id: morador.id,
      nome: morador.nome,
      foto_url: morador.foto_url,
      descricao: morador.unidade ? `Unidade ${morador.unidade}` : "Morador",
    })),
    ...(visitantes || []).map((visitante) => ({
      key: `visitante-${visitante.id}`,
      tipo: "visitante",
      id: visitante.id,
      nome: visitante.nome_completo,
      foto_url: visitante.foto_url,
      descricao: visitante.nome_morador || "Visitante",
    })),
    ...(contratados || []).map((contratado) => ({
      key: `contratado-${contratado.id}`,
      tipo: "contratado",
      id: contratado.id,
      nome: contratado.nome_completo,
      foto_url: contratado.foto_url,
      descricao: contratado.nome_morador || "Contratado",
    })),
  ];
  const usuarioFixo = useMemo(() => {
    if (!usuarioPreSelecionado) return null;

    return {
      key: `${usuarioPreSelecionado.tipo}-${usuarioPreSelecionado.id}`,
      tipo: usuarioPreSelecionado.tipo,
      id: usuarioPreSelecionado.id,
      nome: usuarioPreSelecionado.nome,
      foto_url: usuarioPreSelecionado.foto_url,
      descricao: usuarioPreSelecionado.descricao,
    };
  }, [usuarioPreSelecionado]);
  const usuarios = usuarioFixo ? [usuarioFixo] : usuariosBase;
  const usuarioSelecionado = usuarios.find((usuario) => usuario.key === usuarioKey) || null;
  const fotoUsuarioUrl = getAssetUrl(usuarioSelecionado?.foto_url);
  const usuariosFiltrados = usuarios.filter((usuario) => {
    const termo = searchTerm.trim().toLowerCase();
    if (!termo) {
      return true;
    }

    return (
      usuario.nome.toLowerCase().includes(termo) ||
      usuario.tipo.toLowerCase().includes(termo) ||
      (usuario.descricao || "").toLowerCase().includes(termo)
    );
  });

  useEffect(() => {
    if (isOpen) {
      setUsuarioKey(usuarioFixo?.key || "");
      setSearchTerm(usuarioFixo ? `${usuarioFixo.nome} [${usuarioFixo.tipo}]` : "");
      setShowResults(false);
      setFoto(null);
      setPreviewUrl(null);
    }
  }, [isOpen, usuarioFixo]);

  useEffect(() => {
    if (foto) {
      const objectUrl = URL.createObjectURL(foto);
      setPreviewUrl(objectUrl);

      return () => URL.revokeObjectURL(objectUrl);
    }

    setPreviewUrl(fotoUsuarioUrl || null);
    return undefined;
  }, [foto, fotoUsuarioUrl]);

  if (!isOpen) return null;

  const handleSelectUsuario = (usuario) => {
    setUsuarioKey(usuario.key);
    setSearchTerm(`${usuario.nome} [${usuario.tipo}]`);
    setShowResults(false);
    setFoto(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!listaDispositivos.length || !usuarioSelecionado) return;

    try {
      setEnviando(true);
      const fotoOrigem = foto || (await criarArquivoDaFotoDoUsuario(usuarioSelecionado));
      if (!fotoOrigem) {
        throw new Error("Esse usuário não tem foto cadastrada. Envie uma foto manualmente.");
      }

      const fotoTratada = await redimensionarFoto(fotoOrigem);
      const fd = new FormData();
      fd.append("usuario_tipo", usuarioSelecionado.tipo);
      fd.append("usuario_id", String(usuarioSelecionado.id));
      fd.append("foto", fotoTratada);
      let response;

      if (listaDispositivos.length === 1) {
        response = await api.post(`/dispositivos/${dispositivo.id}/usuarios`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        listaDispositivos.forEach((item) => {
          fd.append("dispositivo_ids", String(item.id));
        });
        response = await api.post("/dispositivos/usuarios/lote", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          validateStatus: (status) => status >= 200 && status < 300,
        });
      }

      const sucessos = response.data?.sucessos?.length;
      const falhas = response.data?.falhas?.length;
      const mensagem = sucessos || falhas
        ? `Sucesso em ${sucessos || 0} facial(is)` + (falhas ? `, falha em ${falhas}.` : ".")
        : (response.data?.message || "Usuário enviado para a facial com sucesso.");

      alert(mensagem);
      onClose();
    } catch (error) {
      console.error(error);
      const detail = error.response?.data?.detail;
      const message =
        typeof detail === "string"
          ? detail
          : (typeof detail?.error === "string" ? detail.error : detail?.error?.message)
            || detail?.message
            || error.response?.data?.message;
      alert(message || "Erro ao enviar usuário para a facial.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b bg-gray-50/50">
          <h2 className="text-xl font-bold text-slate-800">
            {isGrupo
              ? `Cadastrar no grupo: ${grupo?.nome || "Grupo"}`
              : `Cadastrar na facial: ${dispositivo?.nome || "—"}`}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-red-500">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-700">
              {isGrupo ? "Destino: grupo facial" : "Destino: facial única"}
            </p>
            <p className="mt-1 text-sm text-slate-500">
              {isGrupo
                ? `Este cadastro será enviado para ${listaDispositivos.length} facial(is) vinculadas ao grupo.`
                : `${dispositivo?.nome || "Facial"} receberá este cadastro.`}
            </p>
          </div>

          {usuarioFixo ? (
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-sm font-semibold text-slate-700">Usuário</p>
              <p className="mt-1 text-sm text-slate-500">
                {usuarioFixo.nome} - {usuarioFixo.tipo}
                {usuarioFixo.descricao ? ` - ${usuarioFixo.descricao}` : ""}
              </p>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Usuário</label>
              <div className="relative">
                <input
                  type="text"
                  required
                  placeholder="Digite o nome do usuário..."
                  className="w-full border border-gray-300 rounded-xl p-3 outline-none bg-white"
                  value={searchTerm}
                  onFocus={() => setShowResults(true)}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setUsuarioKey("");
                    setShowResults(true);
                    setFoto(null);
                  }}
                />
                {showResults && (
                  <div className="absolute z-20 mt-2 max-h-56 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg">
                    {usuariosFiltrados.length > 0 ? (
                      usuariosFiltrados.map((usuario) => (
                        <button
                          key={usuario.key}
                          type="button"
                          onClick={() => handleSelectUsuario(usuario)}
                          className="block w-full border-b border-gray-100 px-4 py-3 text-left hover:bg-gray-50"
                        >
                          <span className="block text-sm font-medium text-slate-700">{usuario.nome}</span>
                          <span className="block text-xs text-slate-500">
                            {usuario.tipo} {usuario.descricao ? `• ${usuario.descricao}` : ""}
                          </span>
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-sm italic text-gray-500">
                        Nenhum usuário encontrado
                      </div>
                    )}
                  </div>
                )}
              </div>
              <input
                type="hidden"
                required
                value={usuarioKey}
                onChange={(e) => {
                  setUsuarioKey(e.target.value);
                }}
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Foto</label>
            <input
              type="file"
              accept=".jpg,.jpeg,.png,image/jpeg,image/png"
              className="w-full border border-gray-300 rounded-xl p-3"
              onChange={(e) => setFoto(e.target.files?.[0] || null)}
            />
            <p className="mt-2 text-xs text-gray-500">
              Se o usuário já tiver foto no banco, ela será usada automaticamente. O sistema converte JPG, JPEG ou PNG e reduz a foto para menos de 100 KB antes do envio.
            </p>
            {previewUrl && (
              <img
                src={previewUrl}
                alt={usuarioSelecionado?.nome || "Pré-visualização da foto"}
                className="mt-3 h-28 w-28 rounded-xl object-cover border border-gray-200"
              />
            )}
          </div>

          <button
            disabled={enviando}
            className="w-full bg-slate-900 text-white py-4 rounded-xl font-bold disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {enviando ? "Enviando..." : "Enviar cadastro"}
          </button>
        </form>
      </div>
    </div>
  );
}
